<template>
	<div class="card min-vh-70">
		<div class="card-body">
			<!-- Orders Summary Table -->
			<div class="table-responsive">
				<table class="table table-bordered">
					<thead class="thead-light">
						<tr>
							<th style="width: 50px;"></th>
							<th style="width: 35%;">Receipt Number</th>
							<th style="width: 80px;" class="text-center">Qty</th>
							<th style="width: 100px;" class="text-right">Harga</th>
							<th style="width: 130px;" class="text-right">Handling Fee</th>
							<th style="width: 130px;" class="text-right">Total</th>
						</tr>
					</thead>
				</table>

				<!-- Summary and Detail Rows for each Package -->
				<table class="table table-bordered mb-0" v-for="pkg in mappingDataPerPackage" :key="`pkg-${pkg.orderId}`">
					<tbody>
						<!-- Summary Row -->
						<tr
							class="summary-row cursor-pointer"
							@click="toggleOrder(pkg.orderId)"
						>
							<td class="text-center">
								<i
									class="material-icons"
									style="font-size: 20px; transition: transform 0.3s ease;"
									:style="{ transform: isOrderExpanded(pkg.orderId) ? 'rotate(0deg)' : 'rotate(-90deg)' }"
								>
									{{ isOrderExpanded(pkg.orderId) ? 'expand_more' : 'expand_less' }}
								</i>
							</td>
							<td>
								<div class="d-flex align-items-center">
									<i class="material-icons mr-2" style="font-size: 18px;">shopping_bag</i>
									<span class="font-weight-bold">{{ pkg.orderId }}</span>
								</div>
							</td>
							<td class="text-center">
								<span class="badge badge-light">{{ getPackageTotalQuantity(pkg) }}</span>
							</td>
							<td class="text-right">{{ formatMoney(getPackageSubtotal(pkg)) }}</td>
							<td class="text-right">
								<span v-if="getPackageFee(pkg) > 0">
									{{ formatMoney(getPackageFee(pkg)) }}
								</span>
								<span v-else class="text-muted">-</span>
							</td>
							<td class="text-right">
								<span class="font-weight-bold fs-5">{{ formatMoney(getPackageTotal(pkg)) }}</span>
							</td>
						</tr>

						<!-- Detail Row - Products Table for each Order -->
						<tr
							v-if="isOrderExpanded(pkg.orderId)"
							class="detail-row"
						>
							<td colspan="6" class="p-0">
								<div class="detail-table-wrapper">
									<table class="table table-sm table-hover mb-0">
										<colgroup>
											<col style="width: 50px;">
											<col style="width: 35%;">
											<col style="width: 80px;">
											<col style="width: 100px;">
											<col style="width: 130px;">
											<col style="width: 130px;">
										</colgroup>
										<thead class="thead-light">
											<tr>
												<th>#</th>
												<th>Nama Produk</th>
												<th class="text-center">Qty</th>
												<th class="text-right">Harga</th>
												<th class="text-right">Handling Fee</th>
												<th class="text-right">Total</th>
											</tr>
										</thead>
										<tbody>
											<template v-for="(item, itemIndex) in pkg.items">
												<!-- Unmapped item row -->
												<tr
													v-if="!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0"
													:key="`${pkg.orderId}-${itemIndex}`"
													class="table-warning"
												>
													<td>{{ itemIndex + 1 }}</td>
													<td>
														<strong class="text-danger">{{ item.productName }}</strong>
														<br>
														<small class="text-muted">Belum dimapping</small>
													</td>
													<td class="text-center">
														<span class="badge badge-light">{{ item.quantity }}</span>
													</td>
													<td class="text-right">
														<span class="text-muted">-</span>
													</td>
													<td class="text-right">
														<span class="text-muted">-</span>
													</td>
													<td class="text-right">
														<span class="text-muted">-</span>
													</td>
												</tr>

												<!-- Valid products rows - one row per product -->
												<template v-for="(product, productIndex) in item.selectedProducts">
													<tr
														v-if="item.isValid && item.selectedProducts && item.selectedProducts.length > 0"
														:key="`${pkg.orderId}-${itemIndex}-${productIndex}`"
													>
														<td v-if="productIndex === 0" :rowspan="item.selectedProducts.length" class="align-middle text-center">
															{{ itemIndex + 1 }}
														</td>
														<td>
															<div>
																<strong>{{ product.name }}</strong>
																<br>
																<small class="text-muted">SKU: {{ product.code }}</small>
															</div>
															<small class="text-muted" v-if="productIndex === item.selectedProducts.length - 1">
																Dari file: {{ item.productName }}
															</small>
														</td>
														<td class="text-center">
															<span class="badge badge-light">{{ item.quantity }}</span>
														</td>
														<td class="text-right">
															{{ originalPrice(product) }}
														</td>
														<td class="text-right">
															<div v-if="getItemFee(item) > 0" class="fee-display">
																<div class="fee-amount">
																	{{ formatMoney(product.fee_config.value) }}
																</div>
																<small class="text-muted fee-type">
																	{{ getProductFeeTypeLabel(product) }}
																</small>
															</div>
															<span v-else class="text-muted">-</span>
														</td>
														<td class="text-right">
															<strong>
																{{ formatMoney((product.price * item.quantity) + getProductFee(product, item.quantity)) }}
															</strong>
														</td>
													</tr>
												</template>
											</template>
										</tbody>
									</table>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatMoney, getSimpleProductOriginalPrice } from '@/lib/helper';

export default {
	name: 'Step3OrderSummary',
	data() {
		return {
			expandedOrders: {},
		};
	},
	computed: {
		...mapGetters('importWholesaleOrder', [
			'mappingDataPerPackage',
		]),
	},
	methods: {
		// Toggle expanded state for an order
		toggleOrder(orderId) {
			this.$set(this.expandedOrders, orderId, !this.expandedOrders[orderId]);
		},

		// Check if order is expanded
		isOrderExpanded(orderId) {
			// If explicitly set, return that value
			if (this.expandedOrders[orderId] !== undefined) {
				return this.expandedOrders[orderId];
			}

			// Default: all orders are collapsed
			return false;
		},
		formatMoney,

		// Get fee config from selected products
		getItemFeeConfig(item) {
			if (!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0) {
				return null;
			}

			// Get fee config from first selected product
			// All products in the same item should have the same fee config
			const product = item.selectedProducts[0];
			return product?.fee_config || null;
		},

		// Calculate fee for a single item
		getItemFee(item) {
			if (!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0) {
				return 0;
			}

			// Calculate fee for EACH product in the bundle
			// For bundle: if 1 item has 3 products, fee is calculated 3 times
			return item.selectedProducts.reduce((sum, product) => {
				const feeConfig = product?.fee_config;
				if (!feeConfig) {
					return sum;
				}

				const feeType = feeConfig.type || 'per_order';
				const feeValue = feeConfig.value || 0;

				if (feeType === 'per_order') {
					// Fixed fee per order per SKU/product
					return sum + feeValue;
				}
				// Fee per quantity (per_qty)
				return sum + (feeValue * item.quantity);
			}, 0);
		},

		// Calculate fee per SKU (for bundle display)
		getItemFeePerSku(item) {
			if (!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0) {
				return 0;
			}

			// Get fee from first product (all should have same config)
			const feeConfig = item.selectedProducts[0]?.fee_config;
			if (!feeConfig) {
				return 0;
			}

			const feeType = feeConfig.type || 'per_order';
			const feeValue = feeConfig.value || 0;

			if (feeType === 'per_order') {
				// Fixed fee per order per SKU
				return feeValue;
			}
			// Fee per quantity
			return feeValue * 1; // 1 quantity per product/SKU
		},

		// Calculate fee for a single product
		getProductFee(product, quantity) {
			const feeConfig = product?.fee_config;
			if (!feeConfig) {
				return 0;
			}

			const feeType = feeConfig.type || 'per_order';
			const feeValue = feeConfig.value || 0;

			if (feeType === 'per_order') {
				// Fixed fee per order per SKU
				return feeValue;
			}
			// Fee per quantity
			return feeValue * quantity;
		},

		// Get fee type label for a specific product
		getProductFeeTypeLabel(product) {
			const feeConfig = product?.fee_config;
			if (!feeConfig) {
				return '';
			}

			return feeConfig.type === 'per_order' ? 'per order' : 'per qty';
		},

		// Get fee type label for display
		getFeeTypeLabel(item) {
			const feeConfig = this.getItemFeeConfig(item);
			if (!feeConfig) {
				return '';
			}

			return feeConfig.type === 'per_order' ? 'per order' : 'per qty';
		},

		// Calculate total for a single item (product price * quantity)
		getItemSubtotal(item) {
			if (!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0) {
				return 0;
			}

			// Sum of all selected products price * quantity
			return item.selectedProducts.reduce((sum, product) => {
				const basePrice = getSimpleProductOriginalPrice(product);
				return sum + (basePrice * item.quantity);
			}, 0);
		},

		// Calculate total products price for an item (sum of all product prices)
		getItemProductTotal(item) {
			if (!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0) {
				return 0;
			}

			return item.selectedProducts.reduce((sum, product) => sum + getSimpleProductOriginalPrice(product), 0);
		},

		// Calculate subtotal for a package/order (excluding fees)
		getPackageSubtotal(pkg) {
			return pkg.items.reduce((sum, item) => sum + this.getItemSubtotal(item), 0);
		},

		// Calculate total fees for a package/order
		getPackageFee(pkg) {
			return pkg.items.reduce((sum, item) => sum + this.getItemFee(item), 0);
		},

		// Calculate total for a package/order (subtotal + fees)
		getPackageTotal(pkg) {
			return this.getPackageSubtotal(pkg) + this.getPackageFee(pkg);
		},

		// Calculate total quantity for a package/order
		getPackageTotalQuantity(pkg) {
			return pkg.items.reduce((sum, item) => {
				if (!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0) {
					return sum + item.quantity;
				}

				// For valid items with selectedProducts, multiply quantity by number of products
				return sum + (item.quantity * item.selectedProducts.length);
			}, 0);
		},

		// Save fee data to mappingDataPerPackage
		saveFeeDataToPackages() {
			const updatedPackages = this.mappingDataPerPackage.map((pkg) => {
				const updatedItems = pkg.items.map((item) => {
					const feeConfig = this.getItemFeeConfig(item);

					return {
						...item,
						fee_config: feeConfig,
					};
				});

				return {
					...pkg,
					items: updatedItems,
				};
			});

			this.$store.commit('importWholesaleOrder/SET_MAPPING_DATA', {
				perProduct: this.$store.state.importWholesaleOrder.mappingDataPerProduct,
				perPackage: updatedPackages,
			});
		},

		async fetchServiceFee() {
			try {
				await this.$store.dispatch('importWholesaleOrder/fetchServiceFee');
			} catch (error) {
				console.log('Error fetching service fee:', error);
			}
		},

		// Get the original price of the product
		originalPrice(product) {
			console.log('product: ', product);
			const price = getSimpleProductOriginalPrice(product);
			return formatMoney(price);
		},
	},
	mounted() {
		// Save fee data to mappingDataPerPackage on mount
		this.saveFeeDataToPackages();
		// Fetch service fee from API
		this.fetchServiceFee();
	},
	watch: {
		// mappingDataPerPackage: {
		// 	handler() {
		// 		this.saveFeeDataToPackages();
		// 	},
		// 	deep: true,
		// },
	},
};
</script>

<style scoped>
/* Custom utility classes */
.min-vh-70 {
	min-height: 70vh;
}

.cursor-pointer {
	cursor: pointer;
}

.fs-align-middle {
	vertical-align: middle;
}

/* Summary Row Styling */
.summary-row {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	transition: all 0.3s ease;
	user-select: none;
}

.summary-row:hover {
	background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.summary-row td {
	vertical-align: middle;
	padding: 12px 15px;
}

/* Detail Row Styling */
.detail-row {
	background-color: #ffffff;
}

.detail-table-wrapper {
	background: #ffffff;
	border: 1px solid #e9ecef;
	border-top: none;
	border-radius: 0 0 8px 8px;
	margin: 0;
}

/* Fee Display */
.fee-display {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 2px;
}

.fee-amount {
	font-weight: 600;
	font-size: 0.9rem;
}

.fee-type {
	text-transform: capitalize;
}

/* Table Enhancements */
.table thead th {
	border-top: none;
	border-bottom: 2px solid var(--bs-border-color);
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.table tfoot th {
	border-top: 2px solid var(--bs-border-color);
	border-bottom: none;
}

.table tbody tr {
	transition: background-color 0.2s ease;
}

.table tbody tr:hover {
	background-color: var(--bs-gray-100);
}

/* Badge Styling */
.badge-light {
	background-color: var(--bs-gray-100);
	border: 1px solid var(--bs-border-color);
	font-weight: 600;
	min-width: 40px;
	display: inline-block;
	text-align: center;
}

/* Table Column Widths */
.table.table-bordered > thead > tr > th:nth-child(1),
.table.table-bordered > tbody > tr > td:nth-child(1) {
	width: 50px !important;
	min-width: 50px !important;
	max-width: 50px !important;
}
.table.table-bordered > thead > tr > th:nth-child(2),
.table.table-bordered > tbody > tr > td:nth-child(2) {
	width: 35% !important;
}
.table.table-bordered > thead > tr > th:nth-child(3),
.table.table-bordered > tbody > tr > td:nth-child(3) {
	width: 80px !important;
	min-width: 80px !important;
	max-width: 80px !important;
}
.table.table-bordered > thead > tr > th:nth-child(4),
.table.table-bordered > tbody > tr > td:nth-child(4) {
	width: 100px !important;
	min-width: 100px !important;
	max-width: 100px !important;
}
.table.table-bordered > thead > tr > th:nth-child(5),
.table.table-bordered > tbody > tr > td:nth-child(5) {
	width: 130px !important;
	min-width: 130px !important;
	max-width: 130px !important;
}
.table.table-bordered > thead > tr > th:nth-child(6),
.table.table-bordered > tbody > tr > td:nth-child(6) {
	width: 130px !important;
	min-width: 130px !important;
	max-width: 130px !important;
}
</style>
